/**
 * AIThinkingAnimation — Cinématique de chargement IA (Globe 3D rotatif).
 *
 * Représente un globe 3D en rotation Y continue avec :
 *  - 120 dots distribués uniformément sur la sphère (Fibonacci sphere)
 *  - Connexions dynamiques entre nœuds proches (mesh network)
 *  - Profondeur réelle : dots à l'avant plus gros / lumineux, dots à l'arrière atténués
 *  - Glow cyan/violet + particules bokeh en arrière-plan
 *  - Hub-dots pulsants façon "trafic mondial"
 *  - Status pill rotatif (6 messages)
 *
 * Implémentation : SVG dynamique avec projection orthographique 3D→2D recalculée
 * via requestAnimationFrame. Aucune dépendance Three.js (lourd inutile).
 *
 * Usage :
 *   {loading && <AIThinkingAnimation />}
 */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Brain } from 'lucide-react';

// ─── Génération de N points uniformément distribués sur une sphère unité ───
// Algorithme : Fibonacci sphere (golden angle distribution).
function generateSpherePoints(count, radius = 1) {
    const points = [];
    const phi = Math.PI * (Math.sqrt(5) - 1); // golden angle
    for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2; // [-1, 1]
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = phi * i;
        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;
        points.push({ x: x * radius, y: y * radius, z: z * radius });
    }
    return points;
}

// ─── Distance 3D entre 2 points ───
function dist3D(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// ─── Rotation Y d'un point (autour de l'axe vertical) ───
function rotateY(point, angleRad) {
    const c = Math.cos(angleRad);
    const s = Math.sin(angleRad);
    return {
        x: point.x * c + point.z * s,
        y: point.y,
        z: -point.x * s + point.z * c,
    };
}

const NUM_POINTS = 80;
const RADIUS = 130; // SVG units
const VIEWBOX_SIZE = 320; // SVG viewBox: -160..160 in both x and y (tighter = bigger globe)
const CONNECTION_THRESHOLD = 65; // 3D distance threshold to draw a connection
const HUB_INDICES = [5, 18, 32, 45, 58, 68, 75]; // 7 hub nodes scattered

const STATUS_MESSAGES = [
    "Analyse de vos KPI…",
    "Détection des corrélations transverses…",
    "Croisement signaux faibles…",
    "Identification angles morts…",
    "Synthèse stratégique en cours…",
    "Vérification anti-hallucination…",
];

export default function AIThinkingAnimation() {
    const [rotation, setRotation] = useState(0);
    const [statusIndex, setStatusIndex] = useState(0);
    const rafRef = useRef(null);
    const lastTickRef = useRef(performance.now());

    // ─── Points 3D de base (fixes) + paires de connexion (proches en 3D) ───
    const { basePoints, connections } = useMemo(() => {
        const pts = generateSpherePoints(NUM_POINTS, RADIUS);
        const conns = [];
        for (let i = 0; i < pts.length; i++) {
            for (let j = i + 1; j < pts.length; j++) {
                if (dist3D(pts[i], pts[j]) < CONNECTION_THRESHOLD) {
                    conns.push([i, j]);
                }
            }
        }
        return { basePoints: pts, connections: conns };
    }, []);

    // ─── Animation loop (rotation Y) ───
    useEffect(() => {
        const ROTATION_PERIOD_MS = 30000; // 30s pour 360°
        const tick = (now) => {
            const dt = now - lastTickRef.current;
            lastTickRef.current = now;
            setRotation((r) => (r + (dt / ROTATION_PERIOD_MS) * 2 * Math.PI) % (2 * Math.PI));
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, []);

    // ─── Status message rotation ───
    useEffect(() => {
        const id = setInterval(() => {
            setStatusIndex((i) => (i + 1) % STATUS_MESSAGES.length);
        }, 1600);
        return () => clearInterval(id);
    }, []);

    // ─── Apply rotation to all points + project ───
    const rotatedPoints = useMemo(() => {
        return basePoints.map((p, i) => {
            const r = rotateY(p, rotation);
            return { ...r, originalIndex: i };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rotation, basePoints]);

    // Helper: depth factor 0 (back) → 1 (front)
    const depthFactor = (z) => (z + RADIUS) / (2 * RADIUS);

    return (
        <div
            className="relative w-full h-64 sm:h-80 overflow-hidden rounded-xl bg-gradient-to-br from-[#0a0612] via-[#120821] to-[#0a0612] border border-[#681793]/15"
            data-testid="ai-thinking-animation"
        >
            {/* Starfield bokeh background — large blurred dots */}
            <div className="absolute inset-0 pointer-events-none">
                {[
                    { x: 8, y: 15, c: '#681793', size: 60 },
                    { x: 22, y: 38, c: '#FF914D', size: 40 },
                    { x: 12, y: 70, c: '#A855F7', size: 50 },
                    { x: 4, y: 85, c: '#681793', size: 70 },
                    { x: 90, y: 20, c: '#FF914D', size: 45 },
                    { x: 85, y: 60, c: '#A855F7', size: 35 },
                    { x: 30, y: 88, c: '#FF914D', size: 55 },
                ].map((b, i) => (
                    <motion.div
                        key={`bokeh-${i}`}
                        className="absolute rounded-full blur-2xl"
                        style={{
                            left: `${b.x}%`,
                            top: `${b.y}%`,
                            width: `${b.size}px`,
                            height: `${b.size}px`,
                            background: b.c,
                            opacity: 0.18,
                        }}
                        animate={{
                            opacity: [0.1, 0.3, 0.1],
                            scale: [0.85, 1.1, 0.85],
                        }}
                        transition={{
                            duration: 3 + (i % 3),
                            repeat: Infinity,
                            delay: i * 0.4,
                            ease: 'easeInOut',
                        }}
                    />
                ))}
            </div>

            {/* Small static stars */}
            <div className="absolute inset-0 pointer-events-none">
                {Array.from({ length: 40 }).map((_, i) => {
                    // deterministic pseudo-random
                    const seed = (i * 9301 + 49297) % 233280;
                    const x = (seed * 1.3) % 100;
                    const y = ((seed * 7) % 100);
                    const opacity = 0.2 + ((seed % 80) / 100);
                    return (
                        <div
                            key={`star-${i}`}
                            className="absolute rounded-full bg-white"
                            style={{
                                left: `${x}%`,
                                top: `${y}%`,
                                width: '1.5px',
                                height: '1.5px',
                                opacity,
                                boxShadow: '0 0 3px rgba(255,255,255,0.6)',
                            }}
                        />
                    );
                })}
            </div>

            {/* Globe SVG container */}
            <svg
                viewBox={`-${VIEWBOX_SIZE / 2} -${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
                className="relative w-full h-full"
                preserveAspectRatio="xMidYMid meet"
                style={{ display: 'block' }}
            >
                <defs>
                    {/* Radial gradient for the sphere atmosphere — violet halo */}
                    <radialGradient id="atmosphere" cx="50%" cy="50%" r="50%">
                        <stop offset="55%" stopColor="rgba(104, 23, 147, 0)" />
                        <stop offset="80%" stopColor="rgba(168, 85, 247, 0.32)" />
                        <stop offset="92%" stopColor="rgba(255, 145, 77, 0.16)" />
                        <stop offset="100%" stopColor="rgba(104, 23, 147, 0)" />
                    </radialGradient>
                    {/* Globe inner gradient for fake depth — violet */}
                    <radialGradient id="globeInner" cx="35%" cy="35%" r="65%">
                        <stop offset="0%" stopColor="rgba(168, 85, 247, 0.14)" />
                        <stop offset="100%" stopColor="rgba(104, 23, 147, 0)" />
                    </radialGradient>
                    {/* Line gradient — violet to orange */}
                    <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#A855F7" />
                        <stop offset="50%" stopColor="#FF914D" />
                        <stop offset="100%" stopColor="#A855F7" />
                    </linearGradient>
                    {/* Glow filter for dots */}
                    <filter id="dotGlow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="hubGlow" x="-200%" y="-200%" width="500%" height="500%">
                        <feGaussianBlur stdDeviation="5" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="lineGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="1.5" />
                    </filter>
                </defs>

                {/* Atmosphere halo around the sphere */}
                <circle cx="0" cy="0" r={RADIUS + 28} fill="url(#atmosphere)" />
                {/* Inner globe darker fill (creates depth) */}
                <circle cx="0" cy="0" r={RADIUS} fill="url(#globeInner)" />

                {/* Equator + meridian lines (subtle wireframe) — violet */}
                <ellipse cx="0" cy="0" rx={RADIUS} ry={RADIUS * 0.15 + Math.abs(Math.sin(rotation)) * RADIUS * 0.08}
                    fill="none" stroke="rgba(168, 85, 247, 0.28)" strokeWidth="0.6" />
                <circle cx="0" cy="0" r={RADIUS}
                    fill="none" stroke="rgba(168, 85, 247, 0.32)" strokeWidth="0.8" />
                {/* Rotating meridian (cosmetic vertical ellipse) */}
                <ellipse cx="0" cy="0" rx={RADIUS * Math.abs(Math.cos(rotation))} ry={RADIUS}
                    fill="none" stroke="rgba(168, 85, 247, 0.16)" strokeWidth="0.6" />

                {/* Connections (lines between nearby 3D points after rotation) */}
                {connections.map(([i, j], k) => {
                    const p1 = rotatedPoints[i];
                    const p2 = rotatedPoints[j];
                    const avgZ = (p1.z + p2.z) / 2;
                    const visibility = depthFactor(avgZ);
                    if (visibility < 0.3) return null;
                    const opacity = 0.18 + visibility * 0.72;
                    return (
                        <line
                            key={`line-${k}`}
                            x1={p1.x}
                            y1={p1.y}
                            x2={p2.x}
                            y2={p2.y}
                            stroke="url(#lineGrad)"
                            strokeWidth={1.2 + visibility * 1.8}
                            strokeOpacity={opacity}
                            filter="url(#lineGlow)"
                        />
                    );
                })}

                {/* Back dots (z < 0) — atténuated violet */}
                {rotatedPoints
                    .filter((p) => p.z < 0)
                    .map((p) => {
                        const vis = depthFactor(p.z);
                        const r = 1.8 + vis * 1.5;
                        return (
                            <circle
                                key={`back-${p.originalIndex}`}
                                cx={p.x}
                                cy={p.y}
                                r={r}
                                fill="#A855F7"
                                opacity={0.25 + vis * 0.35}
                            />
                        );
                    })}

                {/* Front dots (z >= 0) — bright violet/orange with glow */}
                {rotatedPoints
                    .filter((p) => p.z >= 0)
                    .map((p) => {
                        const vis = depthFactor(p.z);
                        const isHub = HUB_INDICES.includes(p.originalIndex);
                        const r = isHub ? 5 + vis * 2.5 : 2.5 + vis * 2.8;
                        const fill = isHub ? '#FF914D' : '#C084FC';
                        return (
                            <circle
                                key={`front-${p.originalIndex}`}
                                cx={p.x}
                                cy={p.y}
                                r={r}
                                fill={fill}
                                opacity={0.85 + vis * 0.15}
                                filter={isHub ? "url(#hubGlow)" : "url(#dotGlow)"}
                            />
                        );
                    })}

                {/* Hub pulse rings (only front-facing hubs) */}
                {HUB_INDICES.map((idx) => {
                    const p = rotatedPoints[idx];
                    if (!p || p.z < 0) return null;
                    return (
                        <motion.circle
                            key={`hub-pulse-${idx}`}
                            cx={p.x}
                            cy={p.y}
                            r="3"
                            fill="none"
                            stroke="#FF914D"
                            strokeWidth="1.2"
                            initial={{ scale: 0.5, opacity: 1 }}
                            animate={{ scale: [0.5, 4, 0.5], opacity: [0.9, 0, 0.9] }}
                            transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                delay: (idx % 4) * 0.5,
                                ease: 'easeOut',
                            }}
                            style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                        />
                    );
                })}
            </svg>

            {/* Status pill */}
            <div className="absolute inset-x-0 bottom-3 sm:bottom-4 flex items-center justify-center pointer-events-none z-10">
                <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-black/70 border border-[#FF914D]/30 backdrop-blur-md shadow-[0_0_20px_rgba(255,145,77,0.18)]">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    >
                        <Brain className="w-3.5 h-3.5 text-[#FF914D]" />
                    </motion.div>
                    <motion.span
                        key={statusIndex}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.3 }}
                        className="text-[11px] sm:text-xs font-mono tracking-wide text-white/90"
                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                    >
                        {STATUS_MESSAGES[statusIndex]}
                    </motion.span>
                </div>
            </div>
        </div>
    );
}
